import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  Slider,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import SellRoundedIcon from "@mui/icons-material/SellRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import ZoomOutRoundedIcon from "@mui/icons-material/ZoomOutRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import { getCategories, createCategory, updateCategory, deleteCategory, uploadCategoryImage } from "../../api/adminService";

const LEVELS = [
  {
    level: 1,
    title: "Main Categories",
    singular: "Main Category",
    icon: FolderRoundedIcon,
    emptyText: "No main categories yet",
    emptyHint: "Create your first top-level department.",
  },
  {
    level: 2,
    title: "Subcategories",
    singular: "Subcategory",
    icon: AccountTreeRoundedIcon,
    emptyText: "No subcategories yet",
    emptyHint: "Select a main category, then add one.",
  },
  {
    level: 3,
    title: "Items",
    singular: "Item",
    icon: SellRoundedIcon,
    emptyText: "No items yet",
    emptyHint: "Select a subcategory, then add one.",
  },
];

export default function Categories() {
  const [tree, setTree] = useState({ 1: [], 2: [], 3: [] });
  const [loading, setLoading] = useState({ 1: true, 2: false, 3: false });
  const [selected, setSelected] = useState({ 1: null, 2: null });
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null); // { level, parentCategoryId, parentName, category? }
  const [deleteTarget, setDeleteTarget] = useState(null); // { level, category }
  const [homeCategories, setHomeCategories] = useState([]);
  const [homeLoading, setHomeLoading] = useState(true);
  const [homeSearch, setHomeSearch] = useState("");

  async function reloadHomeCategories() {
    setHomeLoading(true);
    try {
      const data = await getCategories({ level: 2 });
      setHomeCategories(data);
    } finally {
      setHomeLoading(false);
    }
  }

  async function reloadLevel(level, parentCategoryId) {
    setLoading((prev) => ({ ...prev, [level]: true }));
    try {
      const params = { level };
      if (level > 1) params.parentCategoryId = parentCategoryId;
      const data = await getCategories(params);
      setTree((prev) => ({ ...prev, [level]: data }));
    } finally {
      setLoading((prev) => ({ ...prev, [level]: false }));
    }
  }

  useEffect(() => {
    reloadLevel(1).catch(() => setError("Could not load categories. Is the API running?"));
    reloadHomeCategories().catch(() => setError("Could not load home page categories."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selected[1]) {
      reloadLevel(2, selected[1]).catch(() => setError("Could not load subcategories."));
    } else {
      setTree((prev) => ({ ...prev, 2: [], 3: [] }));
    }
  }, [selected[1]]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selected[2]) {
      reloadLevel(3, selected[2]).catch(() => setError("Could not load items."));
    } else {
      setTree((prev) => ({ ...prev, 3: [] }));
    }
  }, [selected[2]]); // eslint-disable-line react-hooks/exhaustive-deps

  function refreshAll() {
    reloadLevel(1).catch(() => setError("Could not load categories. Is the API running?"));
    if (selected[1]) reloadLevel(2, selected[1]).catch(() => setError("Could not load subcategories."));
    if (selected[2]) reloadLevel(3, selected[2]).catch(() => setError("Could not load items."));
    reloadHomeCategories().catch(() => setError("Could not load home page categories."));
  }

  async function toggleShowOnHomePage(category, value) {
    setHomeCategories((prev) =>
      prev.map((c) => (c.categoryId === category.categoryId ? { ...c, showOnHomePage: value } : c))
    );
    try {
      await updateCategory(category.categoryId, {
        name: category.name,
        nameArabic: category.nameArabic,
        nameHebrew: category.nameHebrew,
        parentCategoryId: category.parentCategoryId,
        imageUrl: category.imageUrl,
        displayOrder: category.displayOrder,
        isActive: category.isActive,
        showOnHomePage: value,
      });
    } catch (err) {
      setHomeCategories((prev) =>
        prev.map((c) => (c.categoryId === category.categoryId ? { ...c, showOnHomePage: !value } : c))
      );
      setError(err.response?.data || "Could not update this category.");
    }
  }

  function openCreateDialog(level) {
    const parentCategoryId = level === 1 ? null : selected[level - 1];
    const parentName = level === 1 ? null : tree[level - 1].find((c) => c.categoryId === parentCategoryId)?.name;
    setDialog({ level, parentCategoryId, parentName, category: null });
  }

  function openEditDialog(level, category) {
    const parentName =
      level === 1 ? null : tree[level - 1].find((c) => c.categoryId === category.parentCategoryId)?.name;
    setDialog({ level, parentCategoryId: category.parentCategoryId, parentName, category });
  }

  async function handleSave(values, imageFile) {
    const { level, parentCategoryId, category } = dialog;
    const payload = {
      name: values.name,
      nameArabic: category?.nameArabic ?? null,
      nameHebrew: category?.nameHebrew ?? null,
      parentCategoryId: parentCategoryId ?? null,
      imageUrl: values.imageUrl || null,
      displayOrder: Number(values.displayOrder) || 0,
      isActive: values.isActive,
      showOnHomePage: category?.showOnHomePage ?? false,
    };

    try {
      let saved;
      if (category) {
        saved = await updateCategory(category.categoryId, payload);
      } else {
        saved = await createCategory(payload);
      }

      if (imageFile) {
        await uploadCategoryImage(saved.categoryId, imageFile);
      }

      setDialog(null);
      // Deactivating a level 1/2 category cascades to its children on the backend,
      // so refresh every visible level (not just the one being edited) to reflect that.
      refreshAll();
    } catch (err) {
      setError(err.response?.data || "Could not save this category.");
      throw err;
    }
  }

  async function confirmDelete() {
    const { level, category } = deleteTarget;
    try {
      await deleteCategory(category.categoryId);
      const parentForLevel = level === 1 ? undefined : category.parentCategoryId;
      await reloadLevel(level, parentForLevel);
      if (level === 2) await reloadHomeCategories();
      if (level < 3) setSelected((prev) => ({ ...prev, [level]: null }));
      setDeleteTarget(null);
    } catch (err) {
      setError(err.response?.data || "This category could not be deleted.");
      setDeleteTarget(null);
    }
  }

  const mainName = tree[1].find((c) => c.categoryId === selected[1])?.name;
  const subName = tree[2].find((c) => c.categoryId === selected[2])?.name;
  const term = search.trim().toLowerCase();

  function filtered(level) {
    if (!term) return tree[level];
    return tree[level].filter((c) => c.name.toLowerCase().includes(term));
  }

  const parentNameById = useMemo(() => {
    const map = {};
    tree[1].forEach((c) => {
      map[c.categoryId] = c.name;
    });
    return map;
  }, [tree]);

  const homeTerm = homeSearch.trim().toLowerCase();
  const filteredHomeCategories = useMemo(() => {
    const list = homeTerm
      ? homeCategories.filter((c) => c.name.toLowerCase().includes(homeTerm))
      : homeCategories;
    return list.slice().sort((a, b) => {
      const parentA = parentNameById[a.parentCategoryId] || "";
      const parentB = parentNameById[b.parentCategoryId] || "";
      return parentA.localeCompare(parentB) || a.displayOrder - b.displayOrder || a.name.localeCompare(b.name);
    });
  }, [homeCategories, homeTerm, parentNameById]);

  const homeSelectedCount = homeCategories.filter((c) => c.showOnHomePage).length;

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ mb: 3, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" } }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Avatar sx={{ bgcolor: "primary.main", width: 44, height: 44 }}>
            <CategoryRoundedIcon />
          </Avatar>
          <Box>
            <Typography variant="h4">Categories</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage the marketplace category tree — departments, subcategories, and items.
            </Typography>
          </Box>
        </Stack>
        <Tooltip title="Refresh">
          <IconButton
            onClick={refreshAll}
            sx={{ border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
          >
            <RefreshRoundedIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {typeof error === "string" ? error : "Something went wrong."}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 2.5, p: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between" }}>
          <TextField
            placeholder="Search categories…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ minWidth: 280 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch("")}>
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <Breadcrumbs separator="›" sx={{ fontSize: 14 }}>
            <Link
              component="button"
              underline={selected[1] ? "hover" : "none"}
              color={selected[1] ? "text.secondary" : "text.primary"}
              onClick={() => setSelected({ 1: null, 2: null })}
              sx={{ fontWeight: selected[1] ? 400 : 700, cursor: selected[1] ? "pointer" : "default" }}
            >
              All Categories
            </Link>
            {mainName && (
              <Link
                component="button"
                underline={selected[2] ? "hover" : "none"}
                color={selected[2] ? "text.secondary" : "text.primary"}
                onClick={() => setSelected((prev) => ({ ...prev, 2: null }))}
                sx={{ fontWeight: selected[2] ? 400 : 700, cursor: selected[2] ? "pointer" : "default" }}
              >
                {mainName}
              </Link>
            )}
            {subName && (
              <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
                {subName}
              </Typography>
            )}
          </Breadcrumbs>
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        {LEVELS.map((meta) => (
          <Grid size={{ xs: 12, md: 4 }} key={meta.level}>
            <CategoryColumn
              meta={meta}
              items={filtered(meta.level)}
              hasItems={tree[meta.level].length > 0}
              loading={loading[meta.level]}
              enabled={meta.level === 1 || Boolean(selected[meta.level - 1])}
              selectedId={selected[meta.level]}
              onSelect={(id) => meta.level < 3 && setSelected((prev) => ({ ...prev, [meta.level]: id }))}
              onAdd={() => openCreateDialog(meta.level)}
              onEdit={(category) => openEditDialog(meta.level, category)}
              onDelete={(category) => setDeleteTarget({ level: meta.level, category })}
            />
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ mt: 2.5, borderRadius: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ px: 2, py: 1.5, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" } }}
        >
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <Avatar sx={{ bgcolor: "secondary.main", width: 36, height: 36 }}>
              <HomeRoundedIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontSize: 15 }}>
                Home Page Categories
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Choose which subcategories appear in the Categories section on the client website's home page.
              </Typography>
            </Box>
          </Stack>
          <Chip
            size="small"
            label={`${homeSelectedCount} selected`}
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
        </Stack>
        <Divider />
        <Box sx={{ p: 1.5 }}>
          <TextField
            placeholder="Search subcategories…"
            value={homeSearch}
            onChange={(e) => setHomeSearch(e.target.value)}
            size="small"
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
          {homeLoading ? (
            <Stack spacing={1} sx={{ p: 1.5 }}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} variant="rounded" height={48} />
              ))}
            </Stack>
          ) : filteredHomeCategories.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {homeCategories.length === 0 ? "No subcategories yet." : "No matches for this search."}
            </Typography>
          ) : (
            <List disablePadding>
              {filteredHomeCategories.map((category) => (
                <ListItem key={category.categoryId} divider sx={{ py: 0.75 }}>
                  <ListItemAvatar sx={{ minWidth: 44 }}>
                    <Avatar
                      src={category.imageUrl || undefined}
                      sx={{ width: 32, height: 32, bgcolor: "grey.100", color: "text.disabled" }}
                    >
                      <AccountTreeRoundedIcon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={category.name}
                    secondary={parentNameById[category.parentCategoryId] || "Uncategorized"}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                    secondaryTypographyProps={{ fontSize: 12 }}
                  />
                  <Switch
                    checked={Boolean(category.showOnHomePage)}
                    onChange={(e) => toggleShowOnHomePage(category, e.target.checked)}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Paper>

      {dialog && <CategoryDialog dialog={dialog} onClose={() => setDialog(null)} onSave={handleSave} />}

      {deleteTarget && (
        <Dialog open onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
          <DialogTitle>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "error.main", width: 36, height: 36 }}>
                <WarningAmberRoundedIcon fontSize="small" />
              </Avatar>
              <span>Delete {LEVELS[deleteTarget.level - 1].singular.toLowerCase()}?</span>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              You're about to delete <strong>{deleteTarget.category.name}</strong>. This cannot be undone.
              {deleteTarget.level < 3 && " Everything nested underneath it will be removed as well."}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

function CategoryColumn({ meta, items, hasItems, loading, enabled, selectedId, onSelect, onAdd, onEdit, onDelete }) {
  const Icon = meta.icon;

  return (
    <Paper variant="outlined" sx={{ height: "100%", borderRadius: 2, display: "flex", flexDirection: "column" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Icon fontSize="small" sx={{ color: "text.secondary" }} />
          <Typography variant="h6" sx={{ fontSize: 15 }}>
            {meta.title}
          </Typography>
          <Chip
            size="small"
            label={items.length}
            variant="outlined"
            sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
          />
        </Stack>
        <Tooltip title={enabled ? `Add ${meta.singular.toLowerCase()}` : "Select a parent first"}>
          <span>
            <IconButton size="small" color="primary" disabled={!enabled} onClick={onAdd}>
              <AddRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Divider />

      <Box sx={{ minHeight: 320, maxHeight: 480, overflowY: "auto", flex: 1 }}>
        {loading ? (
          <Stack spacing={1} sx={{ p: 1.5 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rounded" height={56} />
            ))}
          </Stack>
        ) : !hasItems ? (
          <Stack spacing={1} sx={{ alignItems: "center", justifyContent: "center", height: "100%", py: 6, px: 2 }}>
            <Icon sx={{ fontSize: 32, color: "text.disabled" }} />
            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "center" }}>
              {enabled ? meta.emptyText : "Select a parent first"}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
              {enabled ? meta.emptyHint : `Pick an item on the left to see its ${meta.title.toLowerCase()}.`}
            </Typography>
            {enabled && (
              <Button size="small" startIcon={<AddRoundedIcon />} onClick={onAdd} sx={{ mt: 0.5 }}>
                Add {meta.singular.toLowerCase()}
              </Button>
            )}
          </Stack>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No matches for this search.
          </Typography>
        ) : (
          <List disablePadding>
            {items.map((category) => (
              <ListItem
                key={category.categoryId}
                disablePadding
                divider
                sx={{
                  "&:hover .row-actions, &:focus-within .row-actions": { opacity: 1 },
                }}
                secondaryAction={
                  <Stack
                    direction="row"
                    spacing={0.25}
                    className="row-actions"
                    sx={{ opacity: 0.35, transition: "opacity .15s" }}
                  >
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => onEdit(category)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => onDelete(category)}>
                        <DeleteOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                }
              >
                <ListItemButton
                  selected={selectedId === category.categoryId}
                  onClick={() => onSelect(category.categoryId)}
                  sx={{
                    pr: 9,
                    py: 1,
                    borderLeft: "3px solid transparent",
                    "&.Mui-selected": {
                      borderLeftColor: "secondary.main",
                      bgcolor: "rgba(99, 102, 241, 0.08)",
                    },
                    "&.Mui-selected:hover": {
                      bgcolor: "rgba(99, 102, 241, 0.12)",
                    },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 52 }}>
                    <Avatar
                      src={category.imageUrl || undefined}
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: "grey.100",
                        color: "text.disabled",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Icon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={category.name}
                    primaryTypographyProps={{ fontWeight: selectedId === category.categoryId ? 700 : 500 }}
                    secondary={
                      <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", mt: 0.25 }}>
                        <Typography variant="caption" color="text.secondary">
                          #{category.displayOrder}
                        </Typography>
                        {!category.isActive && (
                          <Chip
                            label="Inactive"
                            size="small"
                            color="default"
                            variant="outlined"
                            sx={{ height: 16, fontSize: 10, fontWeight: 600 }}
                          />
                        )}
                      </Stack>
                    }
                    secondaryTypographyProps={{ component: "div" }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Paper>
  );
}

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;
const CROP_VIEWPORT_SIZE = 200;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const MIN_OUTPUT_SIZE = 256;
const MAX_OUTPUT_SIZE = 1024;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function CategoryDialog({ dialog, onClose, onSave }) {
  const { level, parentName, category } = dialog;
  const meta = LEVELS[level - 1];
  const [name, setName] = useState(category?.name || "");
  const [imageUrl, setImageUrl] = useState(category?.imageUrl || "");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(category?.imageUrl || "");
  const [imageError, setImageError] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [showUrlField, setShowUrlField] = useState(false);
  const [displayOrder, setDisplayOrder] = useState(category?.displayOrder ?? 0);
  const [isActive, setIsActive] = useState(category?.isActive ?? true);
  const [saving, setSaving] = useState(false);

  const [cropFile, setCropFile] = useState(null);
  const [cropSrc, setCropSrc] = useState("");
  const [naturalSize, setNaturalSize] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const cropImgRef = useRef(null);
  const dragStateRef = useRef(null);

  const baseScale = naturalSize ? CROP_VIEWPORT_SIZE / Math.min(naturalSize.width, naturalSize.height) : 1;
  const dispScale = baseScale * zoom;
  const dispWidth = naturalSize ? naturalSize.width * dispScale : 0;
  const dispHeight = naturalSize ? naturalSize.height * dispScale : 0;
  const maxPanX = Math.max(0, (dispWidth - CROP_VIEWPORT_SIZE) / 2);
  const maxPanY = Math.max(0, (dispHeight - CROP_VIEWPORT_SIZE) / 2);

  useEffect(() => {
    setPan((prev) => ({ x: clamp(prev.x, -maxPanX, maxPanX), y: clamp(prev.y, -maxPanY, maxPanY) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, naturalSize]);

  function applyImageFile(file) {
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setImageError("Only JPG, PNG, or WEBP images are allowed.");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setImageError("Image must be 10MB or smaller.");
      return;
    }
    setImageError("");
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setNaturalSize(null);
    setCropFile(file);
    setCropSrc(URL.createObjectURL(file));
  }

  function handleImageChange(e) {
    applyImageFile(e.target.files?.[0]);
    e.target.value = "";
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragActive(false);
    applyImageFile(e.dataTransfer.files?.[0]);
  }

  function handleRemoveImage(e) {
    e.stopPropagation();
    setImageFile(null);
    setImageUrl("");
    setImagePreview("");
    setImageError("");
  }

  function handleCropImageLoad(e) {
    setNaturalSize({ width: e.target.naturalWidth, height: e.target.naturalHeight });
  }

  function handlePointerDown(e) {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStateRef.current = { startX: e.clientX, startY: e.clientY, startPan: pan };
  }

  function handlePointerMove(e) {
    if (!dragStateRef.current) return;
    const { startX, startY, startPan } = dragStateRef.current;
    setPan({
      x: clamp(startPan.x + (e.clientX - startX), -maxPanX, maxPanX),
      y: clamp(startPan.y + (e.clientY - startY), -maxPanY, maxPanY),
    });
  }

  function handlePointerUp(e) {
    dragStateRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // pointer may already be released
    }
  }

  function cancelCrop() {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc("");
    setCropFile(null);
    setNaturalSize(null);
  }

  async function confirmCrop() {
    if (!cropImgRef.current || !naturalSize) return;

    const cropDiameterSourcePx = Math.min(naturalSize.width, naturalSize.height) / zoom;
    const outputSize = Math.round(clamp(cropDiameterSourcePx, MIN_OUTPUT_SIZE, MAX_OUTPUT_SIZE));
    const canvas = document.createElement("canvas");
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext("2d");
    const k = outputSize / CROP_VIEWPORT_SIZE;

    ctx.save();
    ctx.translate(outputSize / 2 + pan.x * k, outputSize / 2 + pan.y * k);
    ctx.scale(dispScale * k, dispScale * k);
    ctx.drawImage(cropImgRef.current, -naturalSize.width / 2, -naturalSize.height / 2, naturalSize.width, naturalSize.height);
    ctx.restore();

    const outputType = ALLOWED_IMAGE_TYPES.includes(cropFile.type) ? cropFile.type : "image/jpeg";
    const quality = outputType === "image/jpeg" ? 0.92 : undefined;
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, outputType, quality));
    if (!blob) return;

    const ext = outputType === "image/png" ? "png" : outputType === "image/webp" ? "webp" : "jpg";
    const finalFile = new File([blob], `category-image.${ext}`, { type: outputType });

    setImageFile(finalFile);
    setImageUrl("");
    setImagePreview(URL.createObjectURL(finalFile));
    cancelCrop();
  }

  async function submit() {
    setSaving(true);
    try {
      await onSave({ name: name.trim(), imageUrl, displayOrder, isActive }, imageFile);
    } catch {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {category ? `Edit ${meta.singular}` : `New ${meta.singular}`}
        </Typography>
        {parentName && (
          <Typography variant="body2" color="text.secondary">
            in {parentName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            {cropSrc ? (
              <Stack spacing={1.5} alignItems="center">
                <Box
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerLeave={handlePointerUp}
                  sx={{
                    position: "relative",
                    width: CROP_VIEWPORT_SIZE,
                    height: CROP_VIEWPORT_SIZE,
                    borderRadius: "50%",
                    overflow: "hidden",
                    bgcolor: "grey.900",
                    cursor: "grab",
                    "&:active": { cursor: "grabbing" },
                    touchAction: "none",
                    border: "2px solid",
                    borderColor: "secondary.main",
                  }}
                >
                  <Box
                    component="img"
                    ref={cropImgRef}
                    src={cropSrc}
                    alt=""
                    onLoad={handleCropImageLoad}
                    draggable={false}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      width: dispWidth || "auto",
                      height: dispHeight || "auto",
                      maxWidth: "none",
                      transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px)`,
                      userSelect: "none",
                      pointerEvents: "none",
                    }}
                  />
                </Box>

                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%", px: 1 }}>
                  <ZoomOutRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  <Slider
                    size="small"
                    min={MIN_ZOOM}
                    max={MAX_ZOOM}
                    step={0.01}
                    value={zoom}
                    onChange={(e, value) => setZoom(value)}
                    disabled={!naturalSize}
                  />
                  <ZoomInRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                  Drag the photo to reposition it, use the slider to zoom.
                </Typography>

                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={cancelCrop}>
                    Cancel
                  </Button>
                  <Button size="small" variant="contained" disabled={!naturalSize} onClick={confirmCrop}>
                    Use Photo
                  </Button>
                </Stack>
              </Stack>
            ) : imagePreview ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ position: "relative", width: 96, height: 96, flexShrink: 0 }}>
                  <Box
                    component="img"
                    src={imagePreview}
                    alt=""
                    sx={{
                      width: 96,
                      height: 96,
                      borderRadius: "50%",
                      objectFit: "cover",
                      display: "block",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  />
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: "50%",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(15, 23, 42, 0.55)",
                      opacity: 0,
                      transition: "opacity .15s",
                      "&:hover": { opacity: 1 },
                    }}
                  >
                    <IconButton
                      component="label"
                      size="small"
                      sx={{ bgcolor: "rgba(255,255,255,0.92)", "&:hover": { bgcolor: "#fff" } }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                      <input type="file" accept="image/*" hidden onChange={handleImageChange} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleRemoveImage}
                      sx={{ bgcolor: "rgba(255,255,255,0.92)", "&:hover": { bgcolor: "#fff" } }}
                    >
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
                <Stack spacing={0.25}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Preview
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Shown as a circle across the app. Hover the photo to change or remove it.
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  component="label"
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={handleDrop}
                  sx={{
                    width: 96,
                    height: 96,
                    flexShrink: 0,
                    borderRadius: "50%",
                    border: "1.5px dashed",
                    borderColor: dragActive ? "secondary.main" : "divider",
                    bgcolor: dragActive ? "rgba(99, 102, 241, 0.06)" : "background.default",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "border-color .15s, background-color .15s",
                  }}
                >
                  <CloudUploadOutlinedIcon sx={{ fontSize: 24, color: "text.secondary" }} />
                  <input type="file" accept="image/*" hidden onChange={handleImageChange} />
                </Box>
                <Stack spacing={0.25}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Click to upload or drag and drop
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    JPG, PNG or WEBP — up to 10MB. Shown as a circle.
                  </Typography>
                </Stack>
              </Stack>
            )}

            {imageError && (
              <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.75 }}>
                {imageError}
              </Typography>
            )}

            {!cropSrc && (
              <>
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={() => setShowUrlField((prev) => !prev)}
                  sx={{ display: "inline-block", mt: 0.75 }}
                >
                  {showUrlField ? "Hide URL field" : "Or paste an image URL instead"}
                </Link>

                {showUrlField && (
                  <TextField
                    label="Image URL"
                    value={imageUrl}
                    onChange={(e) => {
                      setImageUrl(e.target.value);
                      setImageFile(null);
                      setImagePreview(e.target.value);
                      setImageError("");
                    }}
                    fullWidth
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </>
            )}
          </Box>

          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth autoFocus />

          <TextField
            label="Display Order"
            type="number"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
            helperText="Lower numbers appear first"
            fullWidth
          />

          {category && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 1.5, py: 1, borderRadius: 1.5, bgcolor: "action.hover" }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Active
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Inactive categories are hidden from customers
                </Typography>
              </Box>
              <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!name.trim() || saving || Boolean(cropSrc)} onClick={submit}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
